import { ArrowLeft, BookOpen, ExternalLink, Info, MousePointer2 } from "lucide-react";
import type { SneezeStage, Source } from "../content/sneeze";
import type { SelectedStructure } from "./AnatomyScene";

interface EvidencePanelProps {
  stage: SneezeStage;
  selected: SelectedStructure | null;
  structureOptions: SelectedStructure[];
  sources: Source[];
  onSelectStructure: (structure: SelectedStructure) => void;
  onClearSelected: () => void;
  onOpenSources: () => void;
}

const confidenceClass = {
  high: "confidence--high",
  moderate: "confidence--moderate",
  limited: "confidence--limited",
};

export function EvidencePanel({
  stage,
  selected,
  structureOptions,
  sources,
  onSelectStructure,
  onClearSelected,
  onOpenSources,
}: EvidencePanelProps) {
  const stageSources = stage.sourceIds
    .map((id) => sources.find((source) => source.id === id))
    .filter((source): source is Source => Boolean(source));

  if (selected) {
    return (
      <aside className="evidence-panel panel" aria-label="Selected anatomy">
        <div className="panel__label panel__label--split">
          <span>Selected structure</span>
          <button type="button" className="text-button" onClick={onClearSelected}>
            <ArrowLeft size={12} /> Stage
          </button>
        </div>
        <div className="evidence-panel__body">
          <div className="confidence confidence--high">
            <span /> Segmented reference anatomy
          </div>
          <p className="evidence-panel__system">{selected.system}</p>
          <h2>{selected.name}</h2>
          <p className="evidence-panel__summary">{selected.description}</p>
          <div className="source-callout">
            <BookOpen size={14} />
            <div>
              <strong>Geometry source</strong>
              <span>{selected.source}</span>
            </div>
          </div>
          <button type="button" className="secondary-action" onClick={onOpenSources}>
            View licenses and evidence <ExternalLink size={13} />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="evidence-panel panel" aria-labelledby="evidence-stage-title">
      <div className="panel__label panel__label--split">
        <span>Evidence lens</span>
        <span>{String(stage.order).padStart(2, "0")} / 06</span>
      </div>
      <div className="evidence-panel__body">
        <div className={`confidence ${confidenceClass[stage.confidence]}`}>
          <span /> {stage.confidenceLabel}
        </div>
        <p className="evidence-panel__system">{stage.structure.system}</p>
        <h2 id="evidence-stage-title">{stage.structure.name}</h2>
        <p className="evidence-panel__summary">{stage.structure.role}</p>

        <div className="metric-row">
          <div>
            <span>Physiology</span>
            <strong>{stage.metric.value}</strong>
            <small>{stage.metric.label}</small>
          </div>
          <div>
            <span>Chapter</span>
            <strong>{stage.playbackMs / 1000}s</strong>
            <small>deliberate slow motion</small>
          </div>
        </div>

        <div className="evidence-note">
          <Info size={14} />
          <p>{stage.evidenceNote}</p>
        </div>

        <ul className="claim-list">
          {stage.evidence.map((claim) => (
            <li key={claim}>{claim}</li>
          ))}
        </ul>

        <div className="source-stack">
          <span>Primary evidence</span>
          {stageSources.slice(0, 2).map((source) => (
            <a href={source.url} target="_blank" rel="noreferrer" key={source.id}>
              <span>{source.short}</span>
              <ExternalLink size={12} />
            </a>
          ))}
        </div>

        <button type="button" className="secondary-action" onClick={onOpenSources}>
          <BookOpen size={14} /> All sources and myths
        </button>
        <div className="structure-index" aria-label="Visible anatomy structure index">
          <span>Visible anatomy index</span>
          <div>
            {structureOptions.map((structure) => (
              <button
                type="button"
                key={structure.id}
                onClick={() => onSelectStructure(structure)}
              >
                {structure.name}
              </button>
            ))}
          </div>
        </div>
        <p className="select-hint">
          <MousePointer2 size={12} /> Select the scene or use the anatomy index to inspect provenance.
        </p>
      </div>
    </aside>
  );
}
